import React, { useState, useRef } from 'react';
import { Input, Select, Button } from '@fluentui/react-components';
import { TransactionInput, ACTION_OPTIONS, PayeeSuggestion } from '../types';
import { usePayeeSearch } from '../hooks/usePayeeSearch';
import '../styles/register.css';

interface MobileAddTransactionProps {
  open: boolean;
  onClose: () => void;
  onAdd: (input: TransactionInput) => void;
  accountId: number | null;
}

export const MobileAddTransaction: React.FC<MobileAddTransactionProps> = ({
  open, onClose, onAdd, accountId,
}) => {
  const today = new Date().toISOString().split('T')[0];
  const [date, setDate] = useState(today);
  const [action, setAction] = useState('Draft');
  const [payee, setPayee] = useState('');
  const [payment, setPayment] = useState('');
  const [deposit, setDeposit] = useState('');
  const [notes, setNotes] = useState('');

  const { suggestions, isOpen, close, setQuery } = usePayeeSearch(accountId);
  const payeeRef = useRef<HTMLInputElement>(null);

  const handlePayeeChange = (val: string) => {
    setPayee(val);
    setQuery(val.trim());
  };

  const selectSuggestion = (s: PayeeSuggestion) => {
    setPayee(s.payee);
    setAction(s.action);
    if (s.payment) {
      setPayment(String(s.payment));
      setDeposit('');
    } else if (s.deposit) {
      setDeposit(String(s.deposit));
      setPayment('');
    }
    close();
  };

  const handleSubmit = () => {
    if (!date || !payee.trim()) return;
    const p = parseFloat(payment) || null;
    const d = parseFloat(deposit) || null;
    if (!p && !d) return;

    onAdd({
      date,
      action: action || 'Draft',
      payee: payee.trim(),
      payment: p,
      deposit: d,
      notes: notes.trim(),
    });

    // Reset and close
    setDate(today);
    setAction('Draft');
    setPayee('');
    setPayment('');
    setDeposit('');
    setNotes('');
    setQuery('');
    close();
    onClose();
  };

  if (!open) return null;

  return (
    <div className="mobile-add-overlay" onClick={onClose}>
      <div className="mobile-add-sheet" onClick={e => e.stopPropagation()}>
        <h3>Add Transaction</h3>

        <div className="mobile-form-field">
          <label>Date</label>
          <Input type="date" value={date} onChange={(e, d) => setDate(d.value)}
            style={{ width: '100%' }} />
        </div>

        <div className="mobile-form-field">
          <label>Action</label>
          <Select value={action} onChange={(e, d) => setAction(d.value)}
            style={{ width: '100%' }}>
            {ACTION_OPTIONS.map(a => <option key={a} value={a}>{a}</option>)}
          </Select>
        </div>

        <div className="mobile-form-field" style={{ position: 'relative' }}>
          <label>Payee</label>
          <Input
            ref={payeeRef}
            value={payee}
            onChange={(e, d) => handlePayeeChange(d.value)}
            onBlur={() => setTimeout(close, 150)}
            placeholder="Enter payee"
            autoComplete="off"
            style={{ width: '100%' }}
          />
          {isOpen && suggestions.length > 0 && (
            <div style={{
              position: 'absolute', top: '100%', left: 0, right: 0,
              background: '#fff', border: '1px solid #ccc', borderRadius: '8px',
              maxHeight: '150px', overflowY: 'auto', zIndex: 300,
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            }}>
              {suggestions.map(s => (
                <div key={s.payee}
                  onMouseDown={e => { e.preventDefault(); selectSuggestion(s); }}
                  style={{
                    padding: '10px 14px', cursor: 'pointer', fontSize: '14px',
                    borderBottom: '1px solid #f0f0f0',
                  }}>
                  {s.payee}
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: '10px' }}>
          <div className="mobile-form-field" style={{ flex: 1 }}>
            <label>Payment</label>
            <Input type="number" value={payment}
              onChange={(e, d) => setPayment(d.value)}
              placeholder="0.00" style={{ width: '100%' }} />
          </div>
          <div className="mobile-form-field" style={{ flex: 1 }}>
            <label>Deposit</label>
            <Input type="number" value={deposit}
              onChange={(e, d) => setDeposit(d.value)}
              placeholder="0.00" style={{ width: '100%' }} />
          </div>
        </div>

        <div className="mobile-form-field">
          <label>Notes</label>
          <Input value={notes} onChange={(e, d) => setNotes(d.value)}
            placeholder="Optional notes" style={{ width: '100%' }} />
        </div>

        <div className="mobile-form-actions">
          <Button appearance="secondary" onClick={onClose}>Cancel</Button>
          <Button appearance="primary" onClick={handleSubmit}
            style={{ backgroundColor: '#27ae60' }}>
            Add Transaction
          </Button>
        </div>
      </div>
    </div>
  );
};
